import Vue from 'vue'
import { auth, db } from '@/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import VueRouter from 'vue-router'
import HomeView from '../views/HomeView.vue'
import StaffDashboardView from '@/views/StaffDashboardView.vue'
import StaffOnboardCustomerView from '@/views/StaffOnboardCustomerView.vue'
import StaffManageSettingsView from '@/views/StaffManageSettingsView.vue'
import ManageAppProgressView from '@/views/ManageAppProgressView.vue'
import AdminProducts from '@/views/AdminProducts.vue'
import StaffPaymentsView from '@/views/StaffPaymentsView.vue'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('../views/ContactView.vue'),
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('../views/ResetPasswordView.vue'),
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('../views/Privacy.vue'),
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('../views/Terms.vue'),
  },
  {
    path: '/cookies',
    name: 'cookies',
    component: () => import('../views/Cookies.vue'),
  },
  {
    path: '/security',
    name: 'security',
    component: () => import('../views/SecurityView.vue'),
  },
  {
    path: '/',
    name: 'home',
    component: HomeView
  },
  {
    path: '/staff-dashboard',
    name: 'StaffDashboard',
    component: StaffDashboardView,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff-onboard-customer',
    name: 'StaffOnboardCustomer',
    component: StaffOnboardCustomerView,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff-leads',
    name: 'StaffLeads',
    component: () => import('../views/StaffLeads.vue'),
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff/templates',
    name: 'staff-templates',
    component: () => import('../views/StaffTemplates.vue'),
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff-payments',
    name: 'StaffPayments',
    component: StaffPaymentsView,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff/blog',
    name: 'StaffBlogManage',
    component: () => import('../views/StaffBlogManageView.vue'),
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff-manage-settings',
    name: 'StaffManageSettings',
    component: StaffManageSettingsView,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('../views/AdminDashboardView.vue'),
    meta: { requiresAuth: true, role: 'Admin' }
  },
  {
    path: '/admin/templates',
    name: 'admin-templates',
    component: () => import('../views/AdminTemplates.vue'),
    meta: { requiresAuth: true, role: 'Admin' }
  },
  {
    path: '/manage-app-progress',
    name: 'ManageAppProgress',
    component: ManageAppProgressView,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/admin/revenue-reports',
    name: 'AdminRevenueReports',
    component: () => import('../views/AdminRevenueReportsView.vue'),
    meta: { requiresAuth: true, role: 'Admin' }
  },
  {
    path: '/admin/customers/:id',
    name: 'CustomerDetail',
    component: () => import('../views/CustomerDetailView.vue'),
    props: true,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  // ── Customer portal ─────────────────────────────────────────────
  {
    path: '/dashboard',
    name: 'CustomerDashboard',
    component: () => import('../views/CustomerDashboardView.vue'),
    meta: { requiresAuth: true, role: 'Customer' }
  },
  {
    path: '/billing',
    name: 'Billing',
    component: () => import('../views/BillingView.vue'),
    meta: { requiresAuth: true, role: 'Customer' }
  },
  {
    path: '/customer-settings',
    name: 'CustomerSettings',
    component: () => import('../views/CustomerSettingsView.vue'),
    meta: { requiresAuth: true, role: 'Customer' }
  },
  {
    path: '/admin/products',
    name: 'AdminProducts',
    component: AdminProducts,
    meta: { requiresAuth: true, role: 'Admin' }
  },

  {
    path: '/admin-settings',
    name: 'admin-settings',
    component: () => import('../views/AdminSettingsView.vue'),
    meta: { requiresAuth: true, role: 'Admin' }
  },
  // ── Marketing site ──────────────────────────────────────────────
  {
    path: '/why',
    name: 'why',
    component: () => import('../views/WhyView.vue'),
  },
  {
    path: '/features',
    name: 'features',
    component: () => import('../views/FeaturesView.vue'),
  },
  {
    path: '/demo',
    name: 'demo',
    component: () => import('../views/DemoView.vue'),
  },
  {
    path: '/blog',
    name: 'blog',
    component: () => import('../views/BlogView.vue'),
  },
  {
    path: '/blog/:slug',
    name: 'blog-post',
    component: () => import('../views/BlogPostView.vue'),
  },
  {
    path: '/advertising',
    name: 'advertising',
    component: () => import('../views/AdvertisingView.vue'),
  },
  {
    path: '/documentation',
    name: 'documentation',
    component: () => import('../views/DocumentationView.vue'),
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('../views/HelpCenterView.vue'),
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('../views/AboutView.vue'),
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  // New page → start at the top; back/forward → return to where you were;
  // links with #section → scroll to that section below the fixed menu bar.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { selector: to.hash, offset: { x: 0, y: 96 }, behavior: 'smooth' }
    return { x: 0, y: 0 }
  }
})

// ── Sign-in & role check ──────────────────────────────────────────
// Waits for Firebase to restore the session (so a page refresh doesn't
// bounce you to login), then checks users/{uid}.role (case-insensitive):
//   role 'Staff'    → Staff or Admin     role 'Admin' → Admin only
//   role 'Customer' → Customer only
const authReady = new Promise(resolve => { const off = onAuthStateChanged(auth, () => { off(); resolve() }) })
const roleCache = {}
const norm = (r) => String(r || '').trim().toLowerCase()
async function currentRole(uid) {
  if (!roleCache[uid]) {
    roleCache[uid] = getDoc(doc(db, 'users', uid))
      .then(s => norm(s.exists() ? (s.data().role ?? s.data().type) : ''))
      .catch(err => {
        // Usually permission-denied from Firestore rules. Log it so it isn't silent.
        console.error('[router] Could not read users/' + uid, err)
        delete roleCache[uid]
        return ''
      })
  }
  return roleCache[uid]
}
const homeFor = (role) => ({ admin: '/admin', staff: '/staff-dashboard', customer: '/dashboard' }[role] || '/')

router.beforeEach(async (to, from, next) => {
  if (!to.matched.some(r => r.meta.requiresAuth)) return next()
  await authReady
  const user = auth.currentUser
  if (!user) return next({ path: '/login', query: { redirect: to.fullPath } })
  const need = norm(to.matched.map(r => r.meta.role).filter(Boolean).pop())
  if (!need) return next()
  const role = await currentRole(user.uid)
  const ok = need === 'staff' ? ['staff', 'admin'].includes(role) : role === need
  if (ok) return next()
  const home = homeFor(role)
  return next(home === to.path ? '/' : home)
})
onAuthStateChanged(auth, (u) => { if (!u) Object.keys(roleCache).forEach(k => delete roleCache[k]) })

export default router